// Canonical price reads. Every displayed price resolves from `sku_prices`
// (EUR anchor row) so homepage, catalog and detail pages can never disagree.
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/** All EUR-anchored SKU prices, keyed by sku → cents. */
export function useSkuEurPrices() {
  const { data, isLoading } = useQuery({
    queryKey: ["sku_prices", "EUR", "all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sku_prices")
        .select("sku, amount_minor")
        .eq("currency", "EUR");
      if (error) throw error;
      const map: Record<string, number> = {};
      for (const row of data ?? []) map[row.sku] = Number(row.amount_minor);
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });
  return { prices: data ?? {}, isLoading };
}

/** Resolve a single SKU price in EUR cents, with a static fallback. */
export function useSkuEurCents(sku: string | undefined, fallbackEurCents = 0) {
  const { prices, isLoading } = useSkuEurPrices();
  const value = sku ? prices[sku] : undefined;
  return { eurCents: value ?? fallbackEurCents, resolved: value !== undefined, isLoading };
}

export interface BootcampPriceRow {
  bootcamp_slug: string;
  sku: string;
  eur_cents: number;
}

/** Canonical bootcamp prices keyed by slug, plus the real catalogue floor. */
export function useBootcampPrices() {
  const { data, isLoading } = useQuery({
    queryKey: ["bootcamp_prices_v"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bootcamp_prices_v")
        .select("bootcamp_slug, sku, eur_cents");
      if (error) throw error;
      return (data ?? []) as BootcampPriceRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const bySlug: Record<string, number> = {};
  for (const row of data ?? []) {
    if (row.bootcamp_slug) bySlug[row.bootcamp_slug] = Number(row.eur_cents);
  }

  const paid = Object.values(bySlug).filter((c) => c > 0);
  const floorEurCents = paid.length ? Math.min(...paid) : null;

  return { bySlug, floorEurCents, isLoading };
}

/** Price for one bootcamp slug, falling back to the tier catalogue value. */
export function useBootcampEurCents(slug: string | undefined, fallbackEurCents = 0) {
  const { bySlug, isLoading } = useBootcampPrices();
  const value = slug ? bySlug[slug] : undefined;
  return { eurCents: value ?? fallbackEurCents, resolved: value !== undefined, isLoading };
}

/**
 * Canonical download (playbook) price for one download slug. Reads the same
 * `sku_prices` anchor as every other surface, via `download_prices_v`.
 */
export function useDownloadEurCents(slug: string | null | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: ["download_prices_v", slug],
    enabled: !!slug,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("download_prices_v" as any)
        .select("download_slug, sku, eur_cents")
        .eq("download_slug", slug!)
        .maybeSingle();
      if (error) throw error;
      return data as { download_slug: string; sku: string; eur_cents: number } | null;
    },
    staleTime: 5 * 60 * 1000,
  });

  return {
    eurCents: data ? Number(data.eur_cents) : null,
    sku: data?.sku ?? null,
    isLoading,
  };
}
